// SPDX-License-Identifier: MIT
// SQL Panel Map Control - Button in the map controls area to toggle SQL panel

import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { MapControlButton, MapControlTooltipFactory } from '@kepler.gl/components';
import {
  selectIsSqlPanelOpen,
  selectIsSyncingTables,
  type RootStateWithSqlPanel,
} from '../sql-panel-slice';

// Get the MapControlTooltip component
const MapControlTooltip = MapControlTooltipFactory();

// SQL/Database icon
const SqlIcon = ({ height = '22px' }: { height?: string }) => (
  <svg viewBox='0 0 24 24' height={height} fill='currentColor'>
    <path d='M12 3C7.58 3 4 4.79 4 7V17C4 19.21 7.59 21 12 21S20 19.21 20 17V7C20 4.79 16.42 3 12 3M18 17C18 17.5 15.87 19 12 19S6 17.5 6 17V14.77C7.61 15.55 9.72 16 12 16S16.39 15.55 18 14.77V17M18 12.45C16.7 13.4 14.42 14 12 14C9.58 14 7.3 13.4 6 12.45V9.64C7.47 10.47 9.61 11 12 11C14.39 11 16.53 10.47 18 9.64V12.45M12 9C8.13 9 6 7.5 6 7S8.13 5 12 5 18 6.5 18 7 15.87 9 12 9Z' />
  </svg>
);

// Loading spinner for syncing state
const LoadingSpinner = ({ height = '22px' }: { height?: string }) => (
  <svg
    viewBox='0 0 24 24'
    height={height}
    fill='currentColor'
    style={{ animation: 'spin 1s linear infinite' }}
  >
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <path d='M12 4V2A10 10 0 0 0 2 12H4A8 8 0 0 1 12 4Z' />
  </svg>
);

/**
 * SQL Panel Control - Map control button to toggle the SQL Query Panel
 * Uses Kepler.gl's MapControlButton and MapControlTooltip for consistent styling
 */
export const SqlPanelControl: React.FC = () => {
  const isOpen = useSelector((state: RootStateWithSqlPanel) => selectIsSqlPanelOpen(state));
  const isSyncing = useSelector((state: RootStateWithSqlPanel) => selectIsSyncingTables(state));

  const handleClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    (window as any).toggleSqlPanel?.();
  }, []);

  return (
    <MapControlTooltip
      id='action-sql-panel'
      message={isSyncing ? 'Loading tables...' : 'SQL Query Panel'}
    >
      <MapControlButton
        onClick={handleClick}
        active={isOpen}
        className='map-control-button sql-panel-control'
        data-testid='sql-panel-control'
      >
        {isSyncing ? <LoadingSpinner height='22px' /> : <SqlIcon height='22px' />}
      </MapControlButton>
    </MapControlTooltip>
  );
};

SqlPanelControl.displayName = 'SqlPanelControl';

// Factory function for Kepler.gl component injection
function SqlPanelControlFactory() {
  return SqlPanelControl;
}

// Set deps for factory pattern
(SqlPanelControlFactory as unknown as { deps: unknown[] }).deps = [];

export default SqlPanelControlFactory;
